import { Button, Modal, Textarea, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { PortPicker } from '../../inputs/PortPicker.js';
import { errorCode } from '../personnel/source.js';
import { purchasingError } from './errors.js';
import orders from './Orders.module.css';
import {
  type DeliveryFormValues,
  deliveryForm,
  toDeliveryInput,
  validateDeliveryForm,
} from './orders.js';
import classes from './Purchasing.module.css';
import type { PurchaseOrderEditor } from './source.js';
import type { PurchaseOrderDetail } from './types.js';

/**
 * Taslak siparişin teslim bilgileri (ADR 0040 #179): beklenen teslim, yer, koşullar, garanti ve
 * not. Kalemler ve fiyatlar tekliften gelir; değişmez.
 */
export function DeliveryDialog({
  detail,
  editor,
  onClose,
  onSaved,
}: {
  detail: PurchaseOrderDetail;
  editor: PurchaseOrderEditor;
  onClose: () => void;
  onSaved: (next: PurchaseOrderDetail) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`purchasing.po.detail.${key}`);
  const [values, setValues] = useState<DeliveryFormValues>(() => deliveryForm(detail));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (next: Partial<DeliveryFormValues>) => setValues((v) => ({ ...v, ...next }));
  const fieldError = (field: keyof DeliveryFormValues) =>
    errors[field] ? t(`purchasing.field.${errors[field]}`, { defaultValue: errors[field] }) : null;
  const text = (
    field: Exclude<
      keyof DeliveryFormValues,
      'expectedDelivery' | 'remarks' | 'deliveryLocation' | 'deliveryPortCode'
    >,
  ) => (
    <TextInput
      label={k(`facts.${field}`)}
      value={values[field]}
      maxLength={500}
      error={fieldError(field)}
      onChange={(event) => set({ [field]: event.currentTarget.value })}
    />
  );
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('editTitle')}
      size="lg"
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          const found = validateDeliveryForm(values);
          setErrors(found);
          if (Object.keys(found).length > 0) return;
          setBusy(true);
          try {
            onSaved(await editor.update(detail.id, detail.rowVersion, toDeliveryInput(values)));
          } catch (error) {
            setFailure(errorCode(error));
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className={orders.formGrid} data-even>
          <DayInput
            label={k('expectedDelivery')}
            value={values.expectedDelivery}
            minDate={detail.orderDate}
            clearable
            error={fieldError('expectedDelivery')}
            onChange={(value) => set({ expectedDelivery: value })}
          />
          {/* Teslim yeri liman listesinden; listede yoksa metin (ADR 0048 §6) */}
          <PortPicker
            label={k('facts.deliveryLocation')}
            value={{ name: values.deliveryLocation, code: values.deliveryPortCode }}
            onChange={(port) =>
              set({ deliveryLocation: port.name.slice(0, 120), deliveryPortCode: port.code })
            }
            error={fieldError('deliveryLocation') ?? fieldError('deliveryPortCode')}
          />
          {text('deliveryTerms')}
          {text('paymentTerms')}
          {text('warranty')}
          <Textarea
            wrapperProps={{ 'data-wide': true }}
            label={k('facts.remarks')}
            value={values.remarks}
            maxLength={1000}
            autosize
            minRows={2}
            error={fieldError('remarks')}
            onChange={(event) => set({ remarks: event.currentTarget.value })}
          />
        </div>
        {failure ? (
          <p className={classes.error} role="alert">
            {purchasingError(t, failure, 'po')}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('purchasing.password.back')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Tedarikçi teyidi (ADR 0040 #182): teyit numarası ve teyit edilen teslim günü; parola yok. */
export function ConfirmDialog({
  detail,
  editor,
  onClose,
  onSaved,
}: {
  detail: PurchaseOrderDetail;
  editor: PurchaseOrderEditor;
  onClose: () => void;
  onSaved: (next: PurchaseOrderDetail) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`purchasing.po.detail.${key}`);
  const [supplierRef, setSupplierRef] = useState(detail.supplierRef ?? '');
  const [confirmedDelivery, setConfirmedDelivery] = useState<string | null>(
    detail.expectedDelivery,
  );
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('confirmTitle')}
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          try {
            onSaved(
              await editor.confirm(detail.id, {
                rowVersion: detail.rowVersion,
                supplierRef: supplierRef.trim() || null,
                confirmedDelivery,
              }),
            );
          } catch (error) {
            setFailure(errorCode(error));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className={classes.muted}>{k('confirmBody')}</p>
        <TextInput
          label={k('supplierRef')}
          value={supplierRef}
          maxLength={80}
          mb="sm"
          onChange={(event) => setSupplierRef(event.currentTarget.value)}
        />
        <DayInput
          label={k('confirmedDelivery')}
          value={confirmedDelivery}
          minDate={detail.orderDate}
          clearable
          mb="sm"
          onChange={setConfirmedDelivery}
        />
        {failure ? (
          <p className={classes.error} role="alert">
            {purchasingError(t, failure, 'po')}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('purchasing.password.back')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
